/**
 * Owner bosh sahifasi -- "Kompaniya pulsi" (redesign v2): 4 katta raqam (hire, pipeline, foyda, bir hire narxi),
 * filiallar solishtiruvi, e'tibor signallari, 12 oylik hire, top recruiterlar, pul va voronka.
 * Driver qo'shish bu yerda yo'q (⌘K va Drivers sahifasida) -- owner uchun asosiy ish analitika va boshqaruv.
 */
import { Link, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";
import { dayjs, formatMoney } from "@/lib/utils";
import { useBranchFilter } from "@/app/branchFilter";
import { can, type Permission } from "@/features/auth/types";
import type { BranchRow, FinanceBlock } from "@/features/dashboards/types";
import { periodMonth, prevPeriodMonth } from "@/features/dashboards/api";
import { BranchesTable, PeriodSelect, costPerHire, days, pct } from "@/features/dashboards/blocks";
import { branchRowMenu, recruiterRowMenu } from "@/features/dashboards/menu";
import { num } from "@/features/finance/money";
import { usePersonNav } from "@/features/people/usePersonNav";
import { openInNewTab } from "@/lib/clipboard";
import { KpiCard } from "@/components/ui/card";
import { ScopePicker } from "@/components/ScopePicker";
import { AnimatedNumber, AttentionCard, FunnelCard, Grid, HomeHero, LeaderCard, MonthsCard, More, Section, sampleLine, type AttentionRow } from "./parts";
import { pctChip, signedMoney, useHomeClock } from "./clock";
import type { HomeProps } from "./RecruiterHome";
import { InfoTip } from "@/components/ui/info-tip";

export function OwnerHome({ me, data, period, setPeriod }: HomeProps) {
  const { t } = useTranslation("home");
  const { t: ti } = useTranslation("metrics");
  const { t: tm } = useTranslation("menu");
  const navigate = useNavigate();
  const personNav = usePersonNav();
  const clock = useHomeClock(me.timezone);
  const { branchId, setBranchId } = useBranchFilter();
  const k = data.kpis;
  const month = periodMonth(period);
  const prev = prevPeriodMonth(period);
  const branches = data.branches ?? [];
  const selected = branchId ? branches.find((b) => b.branch_id === branchId) : undefined;
  const money = data.money;
  const counts = data.counts;
  const start = dayjs(`${period}-01`);
  const isCurrentMonth = start.isSame(dayjs(), "month");
  const dayOfMonth = isCurrentMonth ? dayjs().date() : start.daysInMonth();
  const hiresPct = pctChip(k.hires, k.hires_prev);
  const paths = { navigate: (p: string) => navigate(p), openNewTab: openInNewTab, profile: personNav, can: (perm: Permission) => can(me, perm) };
  // Karta ortidagi ro'yxat: tanlangan filial va oy bilan (2026-10-18)
  const link = (path: string, extra: Record<string, string> = {}) =>
    `${path}?${new URLSearchParams({ ...(branchId ? { branch: String(branchId) } : {}), ...extra }).toString()}`;

  const a = data.attention;
  const attention: AttentionRow[] = [];
  if (a.overdue_reminders?.count) attention.push({ key: "overdue", count: a.overdue_reminders.count, text: t("attention.overdue"), tone: "warn", to: "/people" });
  if (a.stale.count) attention.push({ key: "stale", count: a.stale.count, text: t("attention.stale", { days: a.stale.days ?? 7 }), tone: "bad", to: "/drivers?problem=stale" });
  if (a.quiet_recruiters?.count) attention.push({ key: "quiet", count: `${a.quiet_recruiters.count}/${a.quiet_recruiters.total ?? counts?.recruiters ?? 0}`, text: t("attention.quiet", { days: a.quiet_recruiters.days ?? 2 }), sub: sampleLine(a.quiet_recruiters.sample, a.quiet_recruiters.count, t), tone: "bad", to: link("/people", { role: "recruiter", quiet: String(a.quiet_recruiters.days ?? 2) }) });
  if (a.cdl_expiring.count) attention.push({ key: "cdl", count: a.cdl_expiring.count, text: t("attention.cdl"), sub: sampleLine(a.cdl_expiring.sample, a.cdl_expiring.count, t), tone: "warn", to: "/drivers?problem=docs_expiring" });
  if (a.trucks_expiring?.count) attention.push({ key: "trucks", count: a.trucks_expiring.count, text: t("attention.trucks", { days: a.trucks_expiring.days }), tone: "warn", to: "/carriers?trucks=expiring" });
  if (a.pending_approvals) attention.push({ key: "approvals", count: a.pending_approvals.count, text: t("attention.approvals"), tone: a.pending_approvals.count ? "warn" : "good", to: "/approvals" });

  const moneyKpi = (() => {
    if (!money) return <KpiCard to={link("/reports", { period })} info={ti("avgTime")} label={t("kpi.avgTime")} value={days(k.avg_time_to_hire_days)} chip={k.avg_time_to_hire_prev !== null ? days(k.avg_time_to_hire_prev) : undefined} sub={t("kpi.vs", { month: prev })} />;
    const profit = num(money.profit);
    const noFees = num(money.expected) === 0 && num(money.received) === 0;
    return (
      <KpiCard
        to={link("/reports", { tab: "finance", period })}
        info={ti("profit")} label={`${t("kpi.profit")} · ${month}`}
        value={signedMoney(profit, (n) => formatMoney(n))}
        valueTone={profit < 0 ? "danger" : profit > 0 ? "success" : undefined}
        chip={noFees ? t("kpi.noFees") : undefined}
        chipTone="warning"
        sub={t("kpi.moneyNote", { received: formatMoney(num(money.received)), cost: formatMoney(num(money.cost)) })}
      />
    );
  })();

  return (
    <div className="flex flex-col gap-6">
      <HomeHero
        eyebrow={t("eyebrow", { date: clock.date, tashkent: clock.tashkent, dallas: clock.dallas })}
        title={selected ? selected.branch : t("company.title")}
        line={
          counts
            ? selected
              ? t("company.lineBranch", { day: dayOfMonth, days: start.daysInMonth(), recruiters: counts.recruiters, activities: counts.activities_7d })
              : t("company.line", { day: dayOfMonth, days: start.daysInMonth(), recruiters: counts.recruiters, branches: counts.branches ?? branches.length, activities: counts.activities_7d })
            : undefined
        }
        actions={
          <>
            <ScopePicker me={me} />
            <PeriodSelect value={period} onChange={setPeriod} />
          </>
        }
      />

      <Grid className="grid-cols-[repeat(auto-fit,minmax(min(230px,100%),1fr))]">
        <KpiCard
          to={link("/drivers", { hired_month: period })}
          info={ti("hires")} label={`${t("kpi.hires")} · ${month}`}
          value={<AnimatedNumber value={k.hires} />}
          size="lg"
          chip={k.hires_pending ? t("kpi.pending", { count: k.hires_pending }) : hiresPct?.label}
          chipTone={k.hires_pending ? "warning" : (hiresPct?.tone ?? "muted")}
          sub={k.hires_prev ? t("kpi.prevIn", { count: k.hires_prev, month: prev }) : t("kpi.noPrev", { month: prev })}
        />
        <KpiCard to={link("/pipeline")} info={ti("inPipeline")} label={t("kpi.inPipeline")} value={<AnimatedNumber value={k.in_pipeline} />} size="lg" chip={t("kpi.submittedN", { count: k.submitted })} sub={t("kpi.thisMonth")} />
        {moneyKpi}
        <KpiCard
          to={link("/budget", { period })}
          info={ti("costPerHire")} label={t("kpi.costPerHire")}
          value={money?.cost_per_hire != null ? formatMoney(money.cost_per_hire) : selected?.cost_per_hire != null ? formatMoney(selected.cost_per_hire) : costPerHire(branches)}
          size="lg"
          chip={k.retention_30 !== null ? t("kpi.retentionChip", { pct: pct(k.retention_30) }) : undefined}
          chipTone="success"
          sub={t("kpi.costNote")}
        />
      </Grid>

      <Grid className="grid-cols-1 xl:grid-cols-3">
        <Section
          className="xl:col-span-2"
          flush
          title={t("branches.title")}
          sub={month}
          action={
            selected ? (
              <button type="button" onClick={() => setBranchId(null)} className="text-primary hover:underline">
                {t("branches.back")}
              </button>
            ) : (
              <More to="/reports?tab=branches">{t("branches.compare")}</More>
            )
          }
        >
          <BranchesTable
            rows={branches}
            onRowClick={(b: BranchRow) => setBranchId(b.branch_id)}
            menu={(b) =>
              branchRowMenu(
                b,
                {
                  ...paths,
                  openDashboard: () => setBranchId(b.branch_id),
                  openBudget: () => {
                    setBranchId(b.branch_id);
                    navigate("/budget");
                  },
                },
                tm,
              )
            }
          />
          {!selected && branches.length > 1 && <p className="px-5 pt-2 pb-1 text-[12px] text-muted">{t("branches.rowHint")}</p>}
        </Section>
        <AttentionCard rows={attention} />
      </Grid>

      <Grid className="grid-cols-1 xl:grid-cols-3">
        {data.hires_by_month && <MonthsCard months={data.hires_by_month} className="xl:col-span-2" />}
        <LeaderCard
          rows={data.leaderboard}
          title={t("leader.top")}
          sub={month}
          menu={(r) => recruiterRowMenu(r, paths, tm)}
          footer={
            <>
              {counts && <span>{`${counts.recruiters} ${t("manager.cols2.recruiters").toLowerCase()}`}</span>}
              <Link to="/people" className="ml-auto font-medium text-primary hover:underline">
                {t("leader.allPeople")}
              </Link>
            </>
          }
        />
      </Grid>

      <Grid className="grid-cols-1 xl:grid-cols-2">
        {money && <MoneyCard money={money} month={month} />}
        <FunnelCard data={data} month={month} period={period} />
      </Grid>
    </div>
  );
}

function MoneyCard({ money, month }: { money: FinanceBlock; month: string }) {
  const { t } = useTranslation("home");
  const { t: ti } = useTranslation("metrics");
  const cells = [
    { key: "expected", label: t("money.expected"), value: num(money.expected), tone: "ink" },
    { key: "received", label: t("money.received"), value: num(money.received), tone: "ink" },
    { key: "cost", label: t("money.cost"), value: num(money.cost), tone: "ink" },
    { key: "profit", label: t("money.profit"), value: num(money.profit), tone: num(money.profit) < 0 ? "danger" : num(money.profit) > 0 ? "success" : "ink" },
  ] as const;
  return (
    <Section title={t("money.title")} sub={t("money.sub", { month })} action={<More to="/budget">{t("money.finance")}</More>}>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(min(150px,100%),1fr))] gap-3">
        {cells.map((c) => (
          <div key={c.key} className="flex flex-col gap-1.5 rounded-2xl bg-fill px-4 py-3.5">
            <span className="flex items-center gap-1.5 text-[11px] font-semibold tracking-[0.06em] text-muted uppercase">
              {c.label}
              <InfoTip text={ti(c.key)} size={12} />
            </span>
            <span className={`tabular text-[26px] leading-none font-light tracking-[-0.03em] ${c.tone === "danger" ? "text-danger" : c.tone === "success" ? "text-success" : ""}`}>{signedMoney(c.value, (n) => formatMoney(n))}</span>
          </div>
        ))}
      </div>
      {money.no_fee_amount > 0 && (
        <div className="mt-4 flex items-center gap-2.5 text-[13px] text-muted">
          <AlertTriangle size={15} className="shrink-0 text-warning" aria-hidden />
          <span>{t("money.noFees", { count: money.no_fee_amount, month })}</span>
        </div>
      )}
    </Section>
  );
}
